<script>

/**
 * Vue: Wishlist list (wishlist-list)
 * -----------------------------------------------------------------------------
 * List displaying products in wishlist.
 * @param {Boolean} [wishlist_page] - Wishlist list is on wishlist page
 *
 */
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'WishlistList',

  props: {
    wishlistPage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      timeout: {},
      promptTimeout: null,
      promptDuration: 5000,
      itemToRemove: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      switch (this.state) {
        case 'adding':
          return this.$string('product.wishlist.adding')
        case 'added':
          return this.$string('product.wishlist.added')
        default:
          return false
      }
    },
  },

  mounted() {
    this.initWishlist()
    this.getWishlistPageTemplate()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      closeOverlay: 'overlays/close',
    }),

    /**
     * Ensures Wishlist King has initiated globally.
     */
    async initWishlist() {
      let elapsedTime = 0
      await new Promise((resolve) => {
        const wishlistInterval = window.setInterval(() => {
          if (typeof window.WishlistKing === 'undefined' && elapsedTime < 2000) {
            elapsedTime += this.$timing('quick')
            return
          }

          window.clearInterval(wishlistInterval)
          resolve()
        }, this.$timing('quick'))
      })
    },

    /**
     * Ensures Wishlist Page template is loaded
     */
    async getWishlistPageTemplate() {
      let elapsedTime = 0

      await new Promise((resolve) => {
        const container = document.querySelector('[js-wishlist-king="container"]')
        const wishlistInterval = window.setInterval(() => {
          if (!container && elapsedTime < 2000) {
            elapsedTime += this.$timing('quick')
            return
          }

          window.clearInterval(wishlistInterval)
          this.setSelectors()
          this.setNodeSelectors()
          this.setEventListeners()
          resolve()
        }, this.$timing('quick'))
      })
    },

    /**
     * Set selectors.
     */
    setSelectors() {
      this.selectors = {
        item: '[js-wishlist-king="item"]',
        list: '[js-wishlist-king="list"]',
        color: '[js-wishlist-product="color"]',
        checkbox: '[js-wishlist-king="checkbox"]',
        radio: '[js-wishlist-king="radio"]',
      }
    },

    /**
     * Set node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        container: document.querySelectorAll('[js-wishlist-king="container"]'),
        items: [...document.querySelectorAll(this.selectors.item)],
        lists: [...document.querySelectorAll(this.selectors.list)],
        options: [...document.querySelectorAll('[js-wishlist-king="option"]')],
        remove: [...document.querySelectorAll('[js-wishlist-king="remove"]')],
        continue: document.querySelectorAll('[js-wishlist-king="continue"]'),
        prompt: document.querySelector('[js-wishlist-king="prompt"]'),
        promptConfirm: document.querySelector('[js-wishlist-king="prompt-confirm"]'),
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.nodeSelectors.lists.forEach((list) => {
        list.addEventListener('mouseenter', () => {
          list.classList.add(this.$class('active'))
        })

        list.addEventListener('mouseleave', () => {
          list.classList.remove(this.$class('active'))
        })
      })

      this.nodeSelectors.items.forEach((item) => {
        item.addEventListener('mouseleave', () => {
          this.handleItemReset(item)
        })
      })

      this.nodeSelectors.options.forEach((option) => {
        option.addEventListener('click', (event) => {
          this.handleOptionClick(event)
        })
      })

      this.nodeSelectors.remove.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault()
          if (button.classList.contains(this.$class('active'))) {
            this.hidePrompt()
          } else {
            this.resetIcons()
            button.classList.add(this.$class('active'))
            this.handleRemoveProduct(event)
          }
        })
      })

      this.nodeSelectors.continue.forEach((continuebtn) => {
        continuebtn.addEventListener('click', () => {
          this.closeOverlay('wishlistDrawer')
        })
      })

      this.nodeSelectors.promptConfirm.addEventListener('click', async() => {
        this.nodeSelectors.prompt.classList.add(this.$class('updating'))

        if (this.itemToRemove) {
          await this.removeFromWishlist(this.itemToRemove)
          window.location.reload()
        }
      })
    },

    /**
     * Handle item reset.
     * @param {Element} item - DOM Element.
     */
    handleItemReset(item) {
      const checkboxes = [...item.querySelectorAll(this.selectors.checkbox)]
      const radios = [...item.querySelectorAll(this.selectors.radio)]

      checkboxes.forEach((checkbox) => {
        checkbox.checked = false
      })

      radios.forEach((radio) => {
        radio.checked = false
      })
    },

    /**
     * Handle item reset.
     * @param {Object} event - Mouse click event.
     */
    async handleRemoveProduct(event) {
      event.preventDefault()
      event.stopPropagation()

      const item = event.target.closest(this.selectors.item)
      const wishlistId = item.dataset.wkItem

      if (this.wishlistPage) {
        this.removeFromWishListPage(wishlistId)
        return
      }

      await this.removeFromWishlist(wishlistId)
      await this.getWishlistPageTemplate()
    },

    /**
     * Handle option click.
     * @param {Object} event - Mouse click event.
     */
    async handleOptionClick(event) {
      event.preventDefault()
      event.stopPropagation()

      const list = event.target.closest(this.selectors.list)
      list.classList.add(this.$class('selected'))

      const variantId = event.target.dataset.variantId

      await this.handleAddToCart(variantId, event)
    },

    /**
     * Handle add to cart submission.
     * @param {String} variantId - Variant Id.
     * @param {Event} event - Click event.
     */
    async handleAddToCart(variantId, event) {
      if (this.state !== 'ready') {
        return
      }

      window.dpc.sendAddToCartEvent({ product_id: `[${variantId}]` })

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      const item = event.target.closest(this.selectors.item)
      const wishlistId = item.dataset.wkItem
      const colorEl = item.querySelector(this.selectors.color)

      if (colorEl) {
        this.lineItemProperties.colour = colorEl.dataset.productColor
      }

      const list = event.target.closest(this.selectors.list)
      list.innerHTML = `
        <span
          class="
            text-mobile-regular-uc-mobile
            text-desktop-regular-uc-desktop
          "
        >
          ${this.addToCartText}
        </span>
      `

      const items = [
        {
          id: variantId,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (!this.isMobile && this.getComponentExists('cart-drawer')) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.removeFromWishlist(wishlistId)
        this.state = 'ready'

        if (this.wishlistPage) {
          location.reload()
          return
        }

        await this.getWishlistPageTemplate()
      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.error = error.description
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    hidePrompt() {
      this.nodeSelectors.prompt.setAttribute('aria-hidden', 'true')
      this.resetIcons()
    },

    showPrompt() {
      this.nodeSelectors.prompt.classList.remove(this.$class('updating'))
      this.nodeSelectors.prompt.removeAttribute('aria-hidden')
      this.promptTimeout = window.setTimeout(this.hidePrompt.bind(this), this.promptDuration)
    },

    /**
     * Handles removing a product from the wishlist.
     * @param {String} wishlistProductId - wishlist item ID to be removed.
     */
    async removeFromWishlist(wishlistProductId) {
      try {
        await window.WishlistKing.toolkit.removeItem(wishlistProductId)
      } catch (error) {
        throw new Error(error)
      }
    },

    resetIcons() {
      this.nodeSelectors.remove.forEach((el) => {
        el.classList.remove(this.$class('active'))
      })
    },

    removeFromWishListPage(wishlistId) {
      this.itemToRemove = wishlistId

      if (this.promptTimeout) {
        window.clearTimeout(this.promptTimeout)
      }

      this.showPrompt(wishlistId)
    },
  },
}
</script>

<style lang="scss">
@import './wishlist-list';
</style>
